import { Shield, Lock, Database, UserCheck, Mail, AlertCircle, MapPin, Bell, Video, Smartphone, MessageSquare } from 'lucide-react';
import { Meta } from '../components/Meta';

export function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <Meta
        title="Privacy Policy | Mindful Family"
        description="Learn how Mindful Family protects and handles your personal data in compliance with GDPR regulations."
      />

      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex items-center mb-8">
          <Shield className="h-8 w-8 text-accent-text mr-3" />
          <h1 className="text-3xl font-bold text-content">Privacy Policy</h1>
        </div>

        <div className="prose max-w-none">
          <p className="text-content/80 mb-8">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="space-y-12">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Lock className="h-5 w-5 mr-2 text-accent-text" />
                Introduction
              </h2>
              <p className="text-content/80">
                Mindful Family ("we", "our", or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, process, and store your personal information when you use our platform at mindful.family (the "Service"), including our website and mobile applications.
              </p>
            </section>

            {/* Data Collection */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Database className="h-5 w-5 mr-2 text-accent-text" />
                Information We Collect
              </h2>
              <div className="space-y-4">
                <h3 className="font-medium text-content">Personal Information</h3>
                <ul className="list-disc pl-6 text-content/80 space-y-2">
                  <li>Account information (name, email, username)</li>
                  <li>Profile information (avatar, bio, professional credentials)</li>
                  <li>Contact details for practitioners and venue owners</li>
                  <li>Payment information (processed securely through Stripe and RevenueCat)</li>
                  <li>Content you create (posts, comments, recipes, courses)</li>
                  <li>Communications through our messaging system</li>
                  <li>Identity verification documents (for practitioner verification via Yoti)</li>
                </ul>

                <h3 className="font-medium text-content">Automatically Collected Information</h3>
                <ul className="list-disc pl-6 text-content/80 space-y-2">
                  <li>Device information (IP address, browser type, device type, operating system)</li>
                  <li>Usage data (pages visited, actions taken, time spent)</li>
                  <li>Cookies and similar tracking technologies</li>
                  <li>Push notification tokens (if you enable notifications)</li>
                </ul>
              </div>
            </section>

            {/* Location Data */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-accent-text" />
                Location Data
              </h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Our "Near Me" feature uses location data to help you find practitioners, venues, events, and services in your area. We collect and process location data as follows:
                </p>
                <h3 className="font-medium text-content">Types of Location Data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Precise GPS Location:</strong> With your explicit consent, we collect precise GPS coordinates from your device to show you nearby services and enable distance-based search filtering</li>
                  <li><strong>Address Information:</strong> When you manually enter an address or postcode for search purposes</li>
                  <li><strong>IP-Based Location:</strong> Approximate location based on your IP address for regional content</li>
                </ul>
                <h3 className="font-medium text-content">How We Use Location Data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Display nearby practitioners, venues, events, and job listings</li>
                  <li>Calculate distances between you and service providers</li>
                  <li>Filter search results by distance radius</li>
                  <li>Show relevant local content and recommendations</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="font-medium text-blue-800">Your Control</span>
                  </div>
                  <p className="text-blue-800">
                    Location access is optional and requires your explicit consent. You can disable location access at any time through your device settings or browser permissions. The Service will still function without location access, but distance-based features will be unavailable.
                  </p>
                </div>
              </div>
            </section>

            {/* Push Notifications */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Bell className="h-5 w-5 mr-2 text-accent-text" />
                Push Notifications
              </h2>
              <div className="space-y-4 text-content/80">
                <p>
                  We use OneSignal to deliver push notifications to your device. When you opt-in to notifications, we collect:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device push notification tokens</li>
                  <li>Notification preferences and settings</li>
                  <li>Notification interaction data (opens, dismissals)</li>
                </ul>
                <h3 className="font-medium text-content">Types of Notifications</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>New messages from other users</li>
                  <li>Booking confirmations and reminders</li>
                  <li>Course updates and new content</li>
                  <li>Community activity (likes, comments, follows)</li>
                  <li>Special offers and platform updates (with your consent)</li>
                </ul>
                <p>
                  You can manage your notification preferences in your account settings or disable them entirely through your device settings.
                </p>
              </div>
            </section>

            {/* Video & Live Streaming */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Video className="h-5 w-5 mr-2 text-accent-text" />
                Video Content & Live Streaming
              </h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Our platform supports video content and live streaming through Livepeer. When you use these features, we collect:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Video content you upload or stream</li>
                  <li>Stream metadata (duration, quality, viewer counts)</li>
                  <li>Chat messages during live streams</li>
                  <li>Viewer interaction data (joins, leaves, reactions)</li>
                </ul>
                <h3 className="font-medium text-content">Video Data Processing</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Videos are processed and stored through Livepeer's infrastructure</li>
                  <li>Live streams are temporarily stored and may be recorded if the host enables recording</li>
                  <li>Stream recordings are stored according to host preferences and our retention policies</li>
                </ul>
              </div>
            </section>

            {/* Messaging & Chat */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-accent-text" />
                Messaging & Chat Data
              </h2>
              <div className="space-y-4 text-content/80">
                <p>
                  When you use our messaging features, we collect and store:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Direct messages between users</li>
                  <li>Group chat messages in communities and courses</li>
                  <li>Live stream chat messages</li>
                  <li>Message timestamps and read receipts</li>
                </ul>
                <h3 className="font-medium text-content">Message Retention</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Direct messages are retained until deleted by the sender or account deletion</li>
                  <li>Community and group messages are retained as long as the group exists</li>
                  <li>Live stream chat messages may be retained with stream recordings</li>
                  <li>Deleted messages are permanently removed within 30 days</li>
                </ul>
              </div>
            </section>

            {/* In-App Purchases */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Smartphone className="h-5 w-5 mr-2 text-accent-text" />
                In-App Purchases & Subscriptions
              </h2>
              <div className="space-y-4 text-content/80">
                <p>
                  We process payments and subscriptions through multiple providers:
                </p>
                <h3 className="font-medium text-content">Stripe (Web Payments)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payment card details are processed directly by Stripe</li>
                  <li>We receive only transaction confirmations and subscription status</li>
                  <li>We do not store your full card numbers</li>
                </ul>
                <h3 className="font-medium text-content">RevenueCat (Mobile App Purchases)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>In-app purchases through Apple App Store are processed via RevenueCat</li>
                  <li>Purchase history and subscription status are synced to your account</li>
                  <li>Refunds and subscription management follow App Store policies</li>
                </ul>
                <h3 className="font-medium text-content">Payment Data We Store</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Transaction IDs and purchase dates</li>
                  <li>Subscription status and renewal dates</li>
                  <li>Payment provider references (not card details)</li>
                  <li>Purchase history for courses, events, and services</li>
                </ul>
              </div>
            </section>

            {/* Use of Data */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <UserCheck className="h-5 w-5 mr-2 text-accent-text" />
                How We Use Your Information
              </h2>
              <p className="text-content/80 mb-4">We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 text-content/80 space-y-2">
                <li>Providing and maintaining our Service</li>
                <li>Processing your transactions and bookings</li>
                <li>Verifying practitioner credentials and certifications</li>
                <li>Facilitating communication between users</li>
                <li>Sending service-related notifications</li>
                <li>Improving and personalizing our Service</li>
                <li>Ensuring platform safety and security</li>
                <li>Displaying relevant nearby services and content</li>
                <li>Processing subscription and purchase transactions</li>
                <li>Delivering live streaming and video content</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            {/* Legal Basis */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Legal Basis for Processing</h2>
              <p className="text-content/80 mb-4">We process your personal data under the following legal bases:</p>
              <ul className="list-disc pl-6 text-content/80 space-y-2">
                <li><strong>Contract:</strong> Processing necessary for the performance of our contract with you</li>
                <li><strong>Consent:</strong> Processing based on your explicit consent (e.g., location data, push notifications, marketing)</li>
                <li><strong>Legal Obligations:</strong> Processing required to comply with our legal obligations</li>
                <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests (e.g., fraud prevention, platform security)</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Data Sharing and Third Parties</h2>
              <p className="text-content/80 mb-4">We share your information with the following service providers:</p>
              <div className="space-y-4 text-content/80">
                <h3 className="font-medium text-content">Payment Processing</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Stripe</strong> - Web payment processing</li>
                  <li><strong>RevenueCat</strong> - Mobile in-app purchase management</li>
                  <li><strong>Apple</strong> - App Store purchases (via RevenueCat)</li>
                </ul>

                <h3 className="font-medium text-content">Infrastructure & Services</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Supabase</strong> - Database, authentication, and file storage</li>
                  <li><strong>Netlify</strong> - Website hosting and serverless functions</li>
                  <li><strong>Livepeer</strong> - Video processing and live streaming</li>
                  <li><strong>OneSignal</strong> - Push notification delivery</li>
                </ul>

                <h3 className="font-medium text-content">Verification & Communication</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Yoti</strong> - Identity verification for practitioners</li>
                  <li><strong>Resend</strong> - Email delivery service</li>
                </ul>

                <h3 className="font-medium text-content">Other Sharing</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Other users - when you interact through our platform (public profiles, messages, community posts)</li>
                  <li>Practitioners and venue owners - when you make bookings or inquiries</li>
                </ul>
              </div>
              <p className="text-content/80 mt-4">
                We ensure all third-party service providers comply with GDPR and maintain appropriate security measures through data processing agreements.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Data Retention</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account data:</strong> Retained while your account is active and for 30 days after deletion</li>
                  <li><strong>Transaction records:</strong> Retained for 7 years for legal and tax compliance</li>
                  <li><strong>Messages:</strong> Retained until deleted by user or account deletion</li>
                  <li><strong>Location data:</strong> Not stored permanently; used only for real-time search</li>
                  <li><strong>Video content:</strong> Retained according to creator settings and platform policies</li>
                  <li><strong>Verification documents:</strong> Retained for the duration of verification validity</li>
                </ul>
                <p>
                  When you delete your account, we will delete or anonymize your personal data within 30 days, except where we are legally required to retain certain information.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Your Data Protection Rights</h2>
              <p className="text-content/80 mb-4">Under GDPR, you have the following rights:</p>
              <ul className="list-disc pl-6 text-content/80 space-y-2">
                <li><strong>Right to access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to restrict processing:</strong> Request limitation of how we use your data</li>
                <li><strong>Right to data portability:</strong> Receive your data in a portable format</li>
                <li><strong>Right to object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time for consent-based processing</li>
              </ul>
              <p className="text-content/80 mt-4">
                To exercise any of these rights, please contact our Data Protection Officer using the contact details below.
              </p>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Security Measures</h2>
              <p className="text-content/80">
                We implement appropriate technical and organizational measures to protect your personal data, including:
              </p>
              <ul className="list-disc pl-6 text-content/80 space-y-2">
                <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                <li>Secure authentication with row-level security policies</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Staff training on data protection</li>
                <li>Incident response procedures</li>
                <li>Secure payment processing through PCI-compliant providers</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Children's Privacy Protection</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="font-medium text-blue-800">COPPA Compliance</span>
                  </div>
                  <p className="text-blue-800">
                    We comply with the Children's Online Privacy Protection Act (COPPA) and do not knowingly collect personal information from children under 13 without verifiable parental consent.
                  </p>
                </div>
                <p>
                  Users between 13-17 years old may use our Service with parental consent and supervision. Parents can contact us to review, modify, or delete their child's personal information.
                </p>
              </div>
            </section>

            {/* Data Breach Notification */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Data Breach Notification</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  In the event of a data breach that may result in a high risk to your rights and freedoms, we will notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach, as required by GDPR.
                </p>
                <h3 className="font-medium text-content">Notification Process</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We will notify affected users via email within 72 hours</li>
                  <li>We will provide clear information about the nature of the breach</li>
                  <li>We will explain the likely consequences and measures taken</li>
                  <li>We will provide contact information for further inquiries</li>
                </ul>
              </div>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">International Data Transfers</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Your data may be transferred to and processed in countries outside the European Economic Area (EEA), including the United States, where some of our service providers are located:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Supabase (United States)</li>
                  <li>Stripe (United States)</li>
                  <li>RevenueCat (United States)</li>
                  <li>OneSignal (United States)</li>
                  <li>Livepeer (United States)</li>
                </ul>
                <p>
                  We ensure appropriate safeguards are in place through Standard Contractual Clauses, adequacy decisions, and data processing agreements with all providers.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-accent-text" />
                Contact Us & Data Protection Officer
              </h2>
              <p className="text-content/80 mb-4">
                For any questions about this Privacy Policy or to exercise your rights, please contact our Data Protection Officer at:
              </p>
              <div className="mt-4 bg-accent-base/10 rounded-lg p-4">
                <p className="text-content/80">
                  <strong>Data Protection Officer:</strong><br />
                  Email: privacy@mindful.family<br />
                  General Support: support@mindful.family<br />
                  Address: United Kingdom<br />
                  Response Time: We will respond to all inquiries within 30 days
                </p>
              </div>
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Shield className="h-5 w-5 text-green-500 mr-2" />
                  <span className="font-medium text-green-800">Supervisory Authority</span>
                </div>
                <p className="text-green-800">
                  If you are in the UK, you have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk if you believe we have not handled your personal data in accordance with applicable data protection laws.
                </p>
              </div>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-accent-text" />
                Changes to This Policy
              </h2>
              <p className="text-content/80">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page, updating the "Last updated" date, and sending you an email notification for significant changes. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
